import axios from "axios";
import { getActiveApp } from "@/lib/pwaAppConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach the active PWA slug to every API call. The backend's AppResolver
// reads `X-Advoset-App` first (Header → Path → Cookie → Subdomain), so this
// header is what disambiguates `/api/auth/me`, `/api/auth/logout`, and any
// other cross-scope endpoint. Fires from a per-request hook so path/scope
// changes (SPA navigation into a different PWA) are respected without a
// full page reload.
api.interceptors.request.use((config) => {
  try {
    const app = getActiveApp();
    if (app?.identity?.id) {
      config.headers = config.headers || {};
      config.headers["X-Advoset-App"] = app.identity.id;
    }
  } catch (_) {
    // Never let header injection break a request.
  }
  return config;
});

// Centralized disabled-account detection: any protected API returning
// 403 { code: ACCOUNT_DISABLED } switches the app into the recovery UI.
// Normal 401s keep the existing login/authentication behavior.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const d = error?.response?.data?.detail;
    if (error?.response?.status === 403 && d && (d.code === "ACCOUNT_DISABLED" || d.code === "ACCOUNT_SUSPENDED" || d === "ACCOUNT_DISABLED")) {
      window.dispatchEvent(new CustomEvent("advoset:account-disabled", { detail: { code: d.code || "ACCOUNT_DISABLED" } }));
    }
    // Server-side admin console 2FA gate → send the admin SPA to its verify screen.
    if (error?.response?.status === 403 && d && d.code === "TWO_FACTOR_REQUIRED") {
      window.dispatchEvent(new CustomEvent("advoset:two-factor-required"));
    }
    // Sensitive-action step-up: the session is valid but its 2FA stamp is older than
    // the freshness window → let the admin re-verify and come back (no logout).
    if (error?.response?.status === 403 && typeof d === "string" && /^2FA (expired|required for this action)/i.test(d)) {
      window.dispatchEvent(new CustomEvent("advoset:two-factor-stale"));
    }
    // Admin-console absolute lifetime reached (server-authoritative) → the
    // AuthContext clears the admin and the route guard sends them to login.
    if (error?.response?.status === 401 && d && d.code === "ADMIN_SESSION_EXPIRED") {
      window.dispatchEvent(new CustomEvent("advoset:admin-session-expired"));
    }
    return Promise.reject(error);
  }
);
